import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X,
  Calendar,
  Clock,
  User,
  Tag,
  Folder,
  Copy,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckSquare,
  Trash2,
  Activity,
} from 'lucide-react';
import type { Task } from '@/types';
import {
  formatDateFull,
  formatDuration,
  getInitials,
  getAvatarColor,
} from '@/utils/formatters';
import { useProfilesContext } from '@/context/ProfilesContext';
import {
  EditableText,
  StatusSelect,
  PrioritySelect,
  DateRangePicker,
  AssigneesEditor,
  TagsEditor,
} from '@/components/shared/inline-editors';
import { CustomFieldsSection } from '@/components/fields/CustomFieldEditor';
import { calculateTaskDeviations, formatDeviationPercent } from '@/utils/deviations';
import { TimeTrackingEditor } from './TimeTrackingEditor';
import { CommentsSection } from './CommentsSection';
import { AttachmentsSection } from './AttachmentsSection';
import clsx from 'clsx';
import { isCompletedStatus } from '@/constants/taskStatuses';
import { useTaskEnrichment } from '@/hooks/useTaskEnrichment';
import { useSpaces } from '@/hooks/useSpaces';
import { useDatabase } from '@/context/DatabaseContext';
import { useTaskActions } from '@/hooks/useTaskActions';
import { getTaskActivity, getActivityDescription, type ActivityLogEntry } from '@/db/activityLog';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { logger } from '@/utils/logger';
import { useDialogStore } from '@/store/dialogStore';
import { fetchTaskStatuses, type TaskStatusDef } from '@/db/taskStatuses';
import { fetchTagsForContext, createTag, type TagDefinition } from '@/db/tags';
import { useOrganization } from '@/context/OrganizationContext';
import { getListSpaceId } from '@/db/projects';
import { Diamond, ChevronDown } from 'lucide-react';
import { getMilestonesBySpace, assignTaskToMilestone, type Milestone } from '@/db/milestones';

interface TaskDetailProps {
  task: Task;
  onClose: () => void;
}

export function TaskDetail({ task, onClose }: TaskDetailProps) {
  const { tasks } = useDatabase();
  const { containersMap } = useSpaces();
  const { uniqueValues } = useTaskEnrichment({
    tasks,
    containersMap: containersMap || new Map(),
  });
  const { handleUpdateTask: onUpdate, handleDeleteTask: onDelete } = useTaskActions();
  const { orgMemberNames, availableAssignees } = useOrgMembers(uniqueValues.assignees);
  const { resolveDisplayName } = useProfilesContext();
  const { currentOrg } = useOrganization();
  const dialogConfirm = useDialogStore((s) => s.confirm);

  // Fetch custom statuses and scoped tags for this task's list
  const [customStatuses, setCustomStatuses] = useState<TaskStatusDef[]>([]);
  const [scopedTags, setScopedTags] = useState<TagDefinition[]>([]);
  useEffect(() => {
    if (!task.spaceId) return;
    let cancelled = false;
    (async () => {
      const projectId = await getListSpaceId(task.spaceId).catch(() => null);
      const [statuses, tags] = await Promise.all([
        fetchTaskStatuses('list', task.spaceId).catch(() => [] as TaskStatusDef[]),
        fetchTagsForContext({
          orgId: currentOrg?.id,
          spaceId: projectId || undefined,
          listId: task.spaceId,
        }).catch(() => [] as TagDefinition[]),
      ]);
      if (!cancelled) {
        setCustomStatuses(statuses);
        setScopedTags(tags);
      }
    })();
    return () => { cancelled = true; };
  }, [task.spaceId, currentOrg?.id]);

  const availableTags = useMemo(() => {
    const scopedNames = scopedTags.map(t => t.name);
    const all = new Set([...scopedNames, ...uniqueValues.tags]);
    return Array.from(all);
  }, [scopedTags, uniqueValues.tags]);

  // Milestone selector
  const [spaceMilestones, setSpaceMilestones] = useState<Milestone[]>([]);
  const [milestoneOpen, setMilestoneOpen] = useState(false);
  useEffect(() => {
    if (!task.spaceId) return;
    let cancelled = false;
    getMilestonesBySpace(task.spaceId)
      .then(ms => { if (!cancelled) setSpaceMilestones(ms); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [task.spaceId]);

  const currentMilestone = spaceMilestones.find(m => m.id === task.milestoneId) || null;

  const handleMilestoneChange = useCallback(async (milestoneId: string | null) => {
    setMilestoneOpen(false);
    await assignTaskToMilestone(task.id, milestoneId);
    onUpdate(task.id, { milestoneId });
  }, [task.id, onUpdate]);

  const isCompleted = isCompletedStatus(task.status);

  const copyId = () => {
    navigator.clipboard.writeText(task.id);
  };

  // Calculate deviations per assignee
  const deviations = calculateTaskDeviations(task);

  // Quick update handlers
  const handleUpdate = (updates: Partial<Task>) => {
    onUpdate(task.id, updates);
  };

  const handleDelete = async () => {
    await onDelete(task.id);
    onClose();
  };

  const handleAddAssignee = (assignee: string) => {
    const assignees = task.assignees || [];
    if (!assignees.includes(assignee)) {
      handleUpdate({ assignees: [...assignees, assignee] });
    }
  };

  const handleRemoveAssignee = (assignee: string) => {
    const assignees = task.assignees || [];
    handleUpdate({ assignees: assignees.filter((a) => a !== assignee) });
  };

  const handleAddTag = (tag: string) => {
    const tags = task.tags || [];
    if (!tags.includes(tag)) {
      handleUpdate({ tags: [...tags, tag] });
    }
  };

  const handleRemoveTag = (tag: string) => {
    const tags = task.tags || [];
    handleUpdate({ tags: tags.filter((t) => t !== tag) });
  };

  const handleCreateNewTag = useCallback(
    (tagName: string) => {
      if (!task.spaceId) return;
      createTag({
        name: tagName,
        color: null,
        scope: 'list',
        scope_id: task.spaceId,
        order_index: 0,
        created_at: Date.now(),
      }).catch(() => {});
    },
    [task.spaceId]
  );

  // Calculate highlight for due date (memoized to avoid impure function calls during render)
  const isDueDateOverdue = useMemo(() => {
    return typeof task.dueDate === 'number' && task.dueDate < Date.now() && !isCompleted;
  }, [task.dueDate, isCompleted]);

  const handleCustomFieldChange = (fieldId: string, value: import('@/types').CustomFieldValue) => {
    handleUpdate({
      customFields: {
        ...task.customFields,
        [fieldId]: value,
      },
    });
  };

  const canEdit = true;

  // Activity log timeline
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const loadActivity = useCallback(async (taskId: string) => {
    setActivityLoading(true);
    try {
      const entries = await getTaskActivity(taskId, { limit: 15 });
      setActivityLog(entries);
    } catch (err) {
      logger.warn('Failed to load activity log:', err);
      setActivityLog([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (task.id) {
      loadActivity(task.id);
    }
  }, [task.id, loadActivity]);

  return (
    <aside className="w-full max-w-[440px] sm:w-[360px] md:w-[400px] lg:w-[420px] shrink-0 border-l border-border-primary bg-surface-secondary flex flex-col h-full animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
        <div className="flex items-center gap-2">
          {/* Status - Inline editable */}
          <StatusSelect
            value={task.status}
            onChange={(status) => handleUpdate({ status })}
            size="md"
            showLabel={true}
            disabled={!canEdit}
            customStatuses={customStatuses}
          />
          {/* Priority - Inline editable */}
          <PrioritySelect
            value={task.priority ?? null}
            onChange={(priority) => handleUpdate({ priority })}
            size="sm"
            showLabel={true}
            disabled={!canEdit}
          />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={copyId}
            className="p-1.5 rounded hover:bg-surface-hover transition-colors group"
            title="Copia ID"
          >
            <Copy className="w-3.5 h-3.5 text-text-quaternary group-hover:text-text-tertiary" />
          </button>
          <button
            onClick={async () => {
              const confirmed = await dialogConfirm({
                title: 'Elimina task',
                message: `Sei sicuro di voler eliminare "${task.name}"?`,
                confirmLabel: 'Elimina',
                variant: 'danger',
              });
              if (confirmed) handleDelete();
            }}
            className="p-1.5 rounded hover:bg-surface-hover transition-colors group"
            title="Elimina"
          >
            <Trash2 className="w-3.5 h-3.5 text-text-quaternary group-hover:text-accent-rose" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-surface-hover transition-colors group"
          >
            <X className="w-4 h-4 text-text-quaternary group-hover:text-text-tertiary" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Title Section - Inline editable */}
        <div className="px-4 py-4 border-b border-border-primary">
          <EditableText
            value={task.name}
            onChange={(name) => handleUpdate({ name })}
            className={clsx(
              'text-[15px] font-semibold leading-snug block',
              isCompleted ? 'text-text-secondary line-through' : 'text-text-primary'
            )}
            inputClassName="text-[15px] font-semibold"
            disabled={!canEdit}
          />
          <button
            onClick={copyId}
            className="flex items-center gap-1 mt-2 text-[10px] text-text-quaternary hover:text-text-tertiary transition-colors font-mono"
          >
            <span>{task.id}</span>
            <Copy className="w-2.5 h-2.5" />
          </button>
        </div>

        {/* Description - Inline editable */}
        <div className="px-4 py-4 border-b border-border-primary">
          <h3 className="text-[10px] uppercase tracking-wider text-text-quaternary mb-2 font-medium">
            Descrizione
          </h3>
          <EditableText
            value={task.content}
            onChange={(content) => handleUpdate({ content })}
            placeholder="Clicca per aggiungere una descrizione..."
            className="text-[12px] text-text-secondary whitespace-pre-wrap leading-relaxed block"
            inputClassName="text-[12px]"
            multiline={true}
            disabled={!canEdit}
          />
        </div>

        {/* Properties */}
        <div className="px-4 py-4 space-y-3">
          {/* Dates - Range picker */}
          <div className="p-2.5 rounded-lg bg-surface-tertiary/50 border border-border-primary">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-1.5 font-medium">
              <Calendar className="w-3 h-3" />
              Date
            </div>
            <DateRangePicker
              startDate={task.startDate}
              endDate={task.dueDate}
              onChangeStart={(startDate) => handleUpdate({ startDate })}
              onChangeEnd={(dueDate) => handleUpdate({ dueDate })}
              showTime={true}
              disabled={!canEdit}
              highlightEnd={isDueDateOverdue}
            />
          </div>

          {/* Time Tracking */}
          <div className="p-2.5 rounded-lg bg-surface-tertiary/50 border border-border-primary">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-1.5 font-medium">
              <Clock className="w-3 h-3" />
              Tempo stimato / Tempo effettivo
            </div>
            <TimeTrackingEditor task={task} onUpdate={handleUpdate} size="sm" disabled={!canEdit} />
          </div>

          {/* Rolled Up Time */}
          {task.rolledUpTime && task.rolledUpTime > 0 && (
            <div className="grid grid-cols-2 gap-2">
              <PropertyCard
                icon={<TrendingUp className="w-3 h-3" />}
                label="Tempo totale"
                value={formatDuration(task.rolledUpTime)}
              />
            </div>
          )}

          {/* Deviations by Assignee */}
          {deviations.length > 0 && (
            <div className="p-3 rounded-lg bg-surface-tertiary/50 border border-border-primary">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-3 font-medium">
                <TrendingUp className="w-3 h-3" />
                Deviazioni per persona
              </div>
              <div className="space-y-2">
                {deviations.map((deviation) => {
                  const isOver = deviation.deviation > 0;
                  const isUnder = deviation.deviation < 0;
                  const isOnTime = Math.abs(deviation.deviationPercent) <= 5;
                  const deviationName = resolveDisplayName(deviation.assignee);
                  const initials = getInitials(deviationName);
                  const avatarColor = getAvatarColor(deviationName);

                  return (
                    <div
                      key={deviation.assignee}
                      className="flex items-center justify-between p-2 rounded-lg bg-surface-secondary border border-border-primary"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {/* Avatar */}
                        <div
                          className={clsx(
                            'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white shrink-0',
                            avatarColor
                          )}
                        >
                          {initials}
                        </div>
                        {/* Assignee name */}
                        <span className="text-[11px] text-text-secondary font-medium truncate">
                          {deviationName}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {/* Deviation info */}
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="text-text-quaternary">
                            {formatDuration(deviation.estimated)}
                          </span>
                          <span className="text-text-quaternary">→</span>
                          <span className="text-text-secondary font-medium">
                            {formatDuration(deviation.spent)}
                          </span>
                        </div>
                        {/* Deviation indicator */}
                        <div
                          className={clsx(
                            'flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium',
                            isOver && 'bg-accent-rose/15 text-accent-rose',
                            isUnder && 'bg-accent-green/15 text-accent-green',
                            isOnTime && 'bg-surface-tertiary text-text-tertiary'
                          )}
                        >
                          {isOver && <TrendingUp className="w-3 h-3" />}
                          {isUnder && <TrendingDown className="w-3 h-3" />}
                          {isOnTime && <Minus className="w-3 h-3" />}
                          <span>
                            {isOver ? '+' : ''}
                            {formatDuration(Math.abs(deviation.deviation))}
                          </span>
                          <span className="opacity-75">
                            ({formatDeviationPercent(deviation.deviationPercent)})
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Location */}
          <div className="p-3 rounded-lg bg-surface-tertiary/50 border border-border-primary">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-2 font-medium">
              <Folder className="w-3 h-3" />
              Posizione
            </div>
            <div className="flex items-center gap-1.5 text-[11px] flex-wrap">
              <span className="text-text-tertiary">{task.spaceName}</span>
              {task.folderPath &&
                Array.isArray(task.folderPath) &&
                task.folderPath.length > 0 &&
                task.folderPath[0] && (
                  <>
                    <span className="text-text-quaternary">/</span>
                    <span className="text-text-tertiary">{task.folderPath[0]}</span>
                  </>
                )}
              {task.listName && (
                <>
                  <span className="text-text-quaternary">/</span>
                  <span className="text-text-primary font-medium">{task.listName}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Milestone selector */}
        {spaceMilestones.length > 0 && (
          <div className="px-4 py-4 border-t border-border-primary">
            <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-2 font-medium">
              <Diamond className="w-3 h-3" />
              Milestone
            </h3>
            <div className="relative">
              <button
                onClick={() => canEdit && setMilestoneOpen(!milestoneOpen)}
                className={clsx(
                  'w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border text-[11px] transition-colors',
                  currentMilestone
                    ? 'bg-surface-tertiary/50 border-border-primary text-text-primary'
                    : 'bg-surface-tertiary/30 border-border-primary text-text-quaternary',
                  canEdit && 'hover:bg-surface-hover cursor-pointer',
                )}
                disabled={!canEdit}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {currentMilestone ? (
                    <>
                      <Diamond className="w-3 h-3 shrink-0" style={{ color: currentMilestone.color }} />
                      <span className="truncate">{currentMilestone.name}</span>
                    </>
                  ) : (
                    <span>Nessuna milestone</span>
                  )}
                </div>
                <ChevronDown className="w-3 h-3 shrink-0 text-text-quaternary" />
              </button>
              {milestoneOpen && (
                <div className="absolute left-0 top-full mt-1 w-full bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-30 py-1 animate-fade-in">
                  {currentMilestone && (
                    <button
                      onClick={() => handleMilestoneChange(null)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-text-quaternary hover:bg-surface-hover transition-colors"
                    >
                      Rimuovi milestone
                    </button>
                  )}
                  {spaceMilestones.map(m => (
                    <button
                      key={m.id}
                      onClick={() => handleMilestoneChange(m.id)}
                      className={clsx(
                        'w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-surface-hover transition-colors',
                        m.id === task.milestoneId ? 'text-accent-violet font-medium' : 'text-text-primary',
                      )}
                    >
                      <Diamond className="w-3 h-3 shrink-0" style={{ color: m.color }} />
                      <span className="truncate">{m.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assignees - Inline editable */}
        <div className="px-4 py-4 border-t border-border-primary">
          <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-3 font-medium">
            <User className="w-3 h-3" />
            Assegnatari
            <span className="text-text-quaternary">({(task.assignees || []).length})</span>
          </h3>
          {(task.assignees || []).length === 0 && !canEdit ? (
            <span className="text-[11px] text-text-quaternary">Nessun assegnatario</span>
          ) : (
            <AssigneesEditor
              assignees={task.assignees || []}
              availableAssignees={availableAssignees}
              orgMemberNames={orgMemberNames.length > 0 ? orgMemberNames : undefined}
              onAdd={handleAddAssignee}
              onRemove={handleRemoveAssignee}
              size="md"
              maxDisplay={10}
              disabled={!canEdit}
            />
          )}
        </div>

        {/* Tags - Inline editable */}
        <div className="px-4 py-4 border-t border-border-primary">
          <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-3 font-medium">
            <Tag className="w-3 h-3" />
            Tags
            <span className="text-text-quaternary">({(task.tags || []).length})</span>
          </h3>
          {(task.tags || []).length === 0 && !canEdit ? (
            <span className="text-[11px] text-text-quaternary">Nessun tag</span>
          ) : (
            <TagsEditor
              tags={task.tags || []}
              availableTags={availableTags}
              onAdd={handleAddTag}
              onRemove={handleRemoveTag}
              onCreateNew={handleCreateNewTag}
              size="md"
              maxDisplay={10}
              disabled={!canEdit}
            />
          )}
        </div>

        {/* Attachments */}
        <AttachmentsSection
          taskId={task.id}
          attachments={task.attachments || []}
          onUpdate={(attachments) => handleUpdate({ attachments })}
          disabled={!canEdit}
        />

        {/* Custom Fields */}
        <CustomFieldsSection
          task={task}
          customFields={task.customFields || undefined}
          onCustomFieldChange={handleCustomFieldChange}
          disabled={!canEdit}
        />

        {/* Comments Section */}
        <CommentsSection taskId={task.id} />

        {/* Activity Section */}
        <div className="px-4 py-4 border-t border-border-primary">
          <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-3 font-medium">
            <Activity className="w-3 h-3" />
            Attività
          </h3>

          {/* Summary counters */}
          <div className="space-y-1.5 mb-3">
            {task.checklists && Object.keys(task.checklists).length > 0 && (
              <div className="flex items-center gap-2 text-[11px] text-text-tertiary">
                <CheckSquare className="w-3.5 h-3.5" />
                <span>Checklist presente</span>
              </div>
            )}
          </div>

          {/* Activity timeline */}
          {activityLoading ? (
            <p className="text-[11px] text-text-quaternary">Caricamento cronologia...</p>
          ) : activityLog.length > 0 ? (
            <div className="space-y-0">
              {activityLog.map((entry, idx) => (
                <div
                  key={entry.id}
                  className="relative flex gap-2.5 pb-3 last:pb-0"
                >
                  {/* Timeline line */}
                  {idx < activityLog.length - 1 && (
                    <div className="absolute left-[5px] top-3 bottom-0 w-px bg-border-primary" />
                  )}
                  {/* Dot */}
                  <span className="relative mt-1 w-[11px] h-[11px] shrink-0 rounded-full border-2 border-border-secondary bg-surface-secondary" />
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-text-secondary leading-snug">
                      {getActivityDescription(entry)}
                    </p>
                    <p className="text-[10px] text-text-quaternary mt-0.5">
                      {formatActivityTime(entry.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-text-quaternary">Nessuna attività registrata</p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-border-primary bg-surface-tertiary/30">
        <div className="flex items-center gap-2 text-[10px] text-text-quaternary">
          <Calendar className="w-3 h-3" />
          <span>Creato il {formatDateFull(task.dateCreated)}</span>
        </div>
      </div>
    </aside>
  );
}

function formatActivityTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'adesso';
  if (minutes < 60) return `${minutes}m fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}g fa`;
  return new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

interface PropertyCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}

function PropertyCard({ icon, label, value, highlight }: PropertyCardProps) {
  return (
    <div className="p-2.5 rounded-lg bg-surface-tertiary/50 border border-border-primary">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary mb-1 font-medium">
        {icon}
        {label}
      </div>
      <p
        className={clsx(
          'text-[11px]',
          highlight ? 'text-accent-rose font-medium' : 'text-text-secondary'
        )}
      >
        {value}
      </p>
    </div>
  );
}
